/**
 * Moon & Jade Fortune chat presentation controller.
 *
 * W7 keeps every conversation in page memory only. It never writes chat or
 * profile data to localStorage/sessionStorage and it never fabricates a reply
 * when the deliberately disconnected transport rejects a request.
 */
(function () {
  'use strict';

  function FortuneChatShell(root) {
    this.root = root;
    this.log = root.querySelector('[data-fortune-chat-log]');
    this.welcome = root.querySelector('[data-fortune-chat-welcome]');
    this.thinking = root.querySelector('[data-fortune-chat-thinking]');
    this.activity = root.querySelector('[data-fortune-chat-activity]');
    this.activityText = root.querySelector('[data-fortune-chat-activity-text]');
    this.profileStatus = root.querySelector('[data-fortune-profile-status]');
    this.composer = root.querySelector('[data-fortune-chat-composer]');
    this.input = root.querySelector('[data-fortune-chat-input]');
    this.sendButton = root.querySelector('[data-fortune-chat-send]');
    this.characterCount = root.querySelector('[data-fortune-chat-count]');
    this.history = root.querySelector('[data-fortune-chat-history]');
    this.newChatButton = root.querySelector('[data-fortune-chat-new]');
    this.exportButton = root.querySelector('[data-fortune-chat-export]');
    this.sidebar = root.querySelector('[data-fortune-chat-sidebar]');
    this.sidebarOpenButton = root.querySelector('[data-fortune-chat-sidebar-open]');
    this.sidebarCloseButton = root.querySelector('[data-fortune-chat-sidebar-close]');
    this.backdrop = root.querySelector('[data-fortune-chat-backdrop]');
    this.liveRegion = root.querySelector('[data-fortune-chat-live]');
    this.starters = Array.from(root.querySelectorAll('[data-fortune-chat-starter]'));
    this.service = window.MoonJadeFortuneChat && window.MoonJadeFortuneChat.service;
    this.maxLength = Number(root.dataset.maxLength) || 2000;
    this.conversations = [];
    this.currentConversationId = null;
    this.sequence = 0;
    this.busy = false;
    this.drawerOpen = false;
    this.previouslyFocused = null;
    this.mobileQuery = window.matchMedia('(max-width: 899px)');
    this.boundDrawerKeydown = this.onDrawerKeydown.bind(this);
    this.init();
  }

  FortuneChatShell.prototype.init = function () {
    var self = this;
    if (!this.log || !this.composer || !this.input) return;

    this.createConversation();
    this.updateComposerState();
    this.syncDrawerMode();

    this.composer.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submitMessage(self.input.value);
    });

    this.input.addEventListener('input', function () {
      self.resizeComposer();
      self.updateComposerState();
    });

    this.input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        self.submitMessage(self.input.value);
      }
    });

    this.starters.forEach(function (starter) {
      starter.addEventListener('click', function () {
        self.submitMessage(starter.dataset.prompt || starter.textContent);
      });
    });

    if (this.newChatButton) this.newChatButton.addEventListener('click', function () { self.startNewConversation(); });
    if (this.exportButton) this.exportButton.addEventListener('click', function () { self.exportCurrentConversation(); });
    if (this.history) this.history.addEventListener('click', function (event) {
      var deleteButton = event.target.closest('[data-delete-conversation-id]');
      if (deleteButton) {
        self.deleteConversation(deleteButton.dataset.deleteConversationId);
        return;
      }
      var button = event.target.closest('[data-conversation-id]');
      if (button) self.switchConversation(button.dataset.conversationId);
    });

    if (this.sidebarOpenButton) this.sidebarOpenButton.addEventListener('click', function () { self.openSidebar(); });
    if (this.sidebarCloseButton) this.sidebarCloseButton.addEventListener('click', function () { self.closeSidebar(true); });
    if (this.backdrop) this.backdrop.addEventListener('click', function () { self.closeSidebar(true); });
    document.addEventListener('keydown', this.boundDrawerKeydown);

    var syncMode = function () { self.syncDrawerMode(); };
    if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', syncMode);
    else this.mobileQuery.addListener(syncMode);

    window.addEventListener('moonjade:fortune-profile', function (event) {
      self.setProfileAvailable(Boolean(event.detail));
    });
    this.setProfileAvailable(Boolean(this.service && this.service.getProfileContext()));
  };

  FortuneChatShell.prototype.createConversation = function () {
    this.sequence += 1;
    var conversation = {
      id: 'page-' + Date.now().toString(36) + '-' + this.sequence,
      title: this.root.dataset.newChatTitle || 'New conversation',
      messages: []
    };
    this.conversations.unshift(conversation);
    this.currentConversationId = conversation.id;
    this.renderHistory();
    this.renderConversation();
    return conversation;
  };

  FortuneChatShell.prototype.currentConversation = function () {
    var id = this.currentConversationId;
    return this.conversations.find(function (conversation) { return conversation.id === id; }) || null;
  };

  FortuneChatShell.prototype.startNewConversation = function () {
    var current = this.currentConversation();
    if (!current || current.messages.length > 0) this.createConversation();
    else this.renderConversation();
    this.setThinking(false);
    this.setActivity(null);
    this.busy = false;
    this.input.value = '';
    this.resizeComposer();
    this.updateComposerState();
    if (this.mobileQuery.matches) this.closeSidebar(false);
    this.input.focus();
  };

  FortuneChatShell.prototype.switchConversation = function (id) {
    if (!this.conversations.some(function (conversation) { return conversation.id === id; })) return;
    this.currentConversationId = id;
    this.busy = false;
    this.setThinking(false);
    this.setActivity(null);
    this.renderHistory();
    this.renderConversation();
    this.updateComposerState();
    if (this.mobileQuery.matches) this.closeSidebar(false);
    this.input.focus();
  };

  FortuneChatShell.prototype.renderHistory = function () {
    var self = this;
    if (!this.history) return;
    this.history.textContent = '';
    this.conversations.forEach(function (conversation) {
      var row = document.createElement('div');
      row.className = 'fortune-chat-sidebar__history-row';
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'fortune-chat-sidebar__history-item';
      button.dataset.conversationId = conversation.id;
      if (conversation.id === self.currentConversationId) {
        button.classList.add('fortune-chat-sidebar__history-item--current');
        button.setAttribute('aria-current', 'true');
      }
      var icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '◌';
      var label = document.createElement('span');
      label.textContent = conversation.title;
      button.append(icon, label);
      var deleteButton = document.createElement('button');
      deleteButton.type = 'button';
      deleteButton.className = 'fortune-chat-sidebar__history-delete';
      deleteButton.dataset.deleteConversationId = conversation.id;
      deleteButton.setAttribute('aria-label', (self.root.dataset.deleteChatLabel || 'Delete chat') + ': ' + conversation.title);
      deleteButton.title = self.root.dataset.deleteChatLabel || 'Delete chat';
      deleteButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4.5 6h11M8 3.5h4M7 6l.6 10h4.8L13 6M8.5 8.5v5M11.5 8.5v5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      row.append(button, deleteButton);
      self.history.appendChild(row);
    });
  };

  FortuneChatShell.prototype.deleteConversation = function (id) {
    var conversation = this.conversations.find(function (entry) { return entry.id === id; });
    if (!conversation) return;
    if (!window.confirm(this.root.dataset.deleteConfirm || 'Delete this temporary chat?')) return;

    this.conversations = this.conversations.filter(function (entry) { return entry.id !== id; });
    if (this.currentConversationId === id) this.currentConversationId = this.conversations.length ? this.conversations[0].id : null;
    if (!this.conversations.length) this.createConversation();
    else {
      this.renderHistory();
      this.renderConversation();
    }
    this.updateComposerState();
    this.announce(this.root.dataset.chatDeleted || 'Chat deleted.');
  };

  FortuneChatShell.prototype.exportCurrentConversation = function () {
    var conversation = this.currentConversation();
    if (!conversation || !conversation.messages.length) {
      this.announce(this.root.dataset.exportEmpty || 'There is no conversation to export.');
      return;
    }

    var locale = this.root.dataset.aiLocale || document.documentElement.lang || 'en';
    var languageName = this.root.dataset.aiLanguageName || locale;
    var exportedAt = new Date();
    var lines = [
      this.root.dataset.exportTitle || 'Moon & Jade — Fortune AI Chat Export',
      '',
      (this.root.dataset.exportedAt || 'Exported') + ': ' + exportedAt.toLocaleString(locale),
      (this.root.dataset.exportLanguage || 'Language') + ': ' + languageName,
      (this.root.dataset.exportPrivacy || 'Privacy') + ': ' + (this.root.dataset.exportNotice || 'Generated locally from this temporary page session.'),
      '',
      '—'.repeat(42),
      ''
    ];

    conversation.messages.forEach(function (message) {
      var role = message.kind === 'user' ? (this.root.dataset.exportRoleUser || 'You') : (message.heading || 'Fortune AI');
      var time = message.timestamp ? new Date(message.timestamp).toLocaleString(locale) : '';
      lines.push((time ? '[' + time + '] ' : '') + role);
      lines.push(String(message.text || ''));
      lines.push('');
    }, this);

    var blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    var stamp = exportedAt.toISOString().slice(0, 19).replace(/[:T]/g, '-');
    link.href = url;
    link.download = 'moon-jade-fortune-ai-' + stamp + '.txt';
    link.hidden = true;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(function () { URL.revokeObjectURL(url); }, 0);
    this.announce(this.root.dataset.exportReady || 'Chat export downloaded.');
  };

  FortuneChatShell.prototype.renderConversation = function () {
    var self = this;
    var conversation = this.currentConversation();
    if (!conversation || !this.log) return;
    this.log.querySelectorAll('[data-generated-message="true"]').forEach(function (message) { message.remove(); });
    this.welcome.hidden = conversation.messages.length > 0;
    conversation.messages.forEach(function (message) { self.renderMessage(message); });
    this.log.scrollTop = this.log.scrollHeight;
  };

  FortuneChatShell.prototype.renderMessage = function (message) {
    var template = this.root.querySelector('[data-fortune-message-template="' + message.kind + '"]');
    if (!template || !this.log) return null;
    var node = template.content.firstElementChild.cloneNode(true);
    var copy = node.querySelector('[data-message-copy]');
    var heading = node.querySelector('[data-message-heading]');
    var time = node.querySelector('[data-message-time]');
    if (copy) copy.textContent = String(message.text || '');
    if (heading) heading.textContent = String(message.heading || '');
    if (time && message.timestamp) {
      var date = new Date(message.timestamp);
      if (!isNaN(date.getTime())) {
        time.dateTime = date.toISOString();
        time.textContent = date.toLocaleTimeString(document.documentElement.lang || undefined, { hour: 'numeric', minute: '2-digit' });
      }
    }
    node.dataset.generatedMessage = 'true';
    this.log.insertBefore(node, this.thinking || null);
    return node;
  };

  FortuneChatShell.prototype.addMessage = function (message) {
    var conversation = this.currentConversation();
    if (!conversation) return;
    conversation.messages.push(message);
    if (message.kind === 'user' && conversation.messages.filter(function (entry) { return entry.kind === 'user'; }).length === 1) {
      var compact = String(message.text || '').replace(/\s+/g, ' ').trim();
      conversation.title = compact ? compact.slice(0, 46) + (compact.length > 46 ? '…' : '') : (this.root.dataset.untitledChat || conversation.title);
      this.renderHistory();
    }
    this.welcome.hidden = true;
    this.renderMessage(message);
    this.log.scrollTop = this.log.scrollHeight;
  };

  FortuneChatShell.prototype.submitMessage = function (rawMessage) {
    var self = this;
    var message = String(rawMessage || '').trim();
    if (!message || this.busy) return;
    if (message.length > this.maxLength) message = message.slice(0, this.maxLength);

    this.addMessage({ kind: 'user', text: message, timestamp: new Date().toISOString() });
    this.input.value = '';
    this.resizeComposer();
    this.busy = true;
    this.updateComposerState();
    this.setThinking(true);

    var conversation = this.currentConversation();
    var request = this.service && this.service.createRequest ? this.service.createRequest({
      conversation_id: conversation ? conversation.id : null,
      message: message
    }) : null;
    var transport = this.service && this.service.sendMessage ? this.service.sendMessage(request) : Promise.reject({ code: 'FORTUNE_CHAT_NOT_CONNECTED' });

    Promise.resolve(transport).then(function (response) {
      if (!response || !response.response) throw new Error('EMPTY_CHAT_RESPONSE');
      self.addMessage({ kind: 'assistant', text: response.response, timestamp: new Date().toISOString() });
    }).catch(function (error) {
      if (error && error.code === 'FORTUNE_CHAT_NOT_CONNECTED') {
        self.addMessage({
          kind: 'unavailable',
          heading: self.root.dataset.unavailableHeading,
          text: self.root.dataset.unavailableText
        });
        self.announce(self.root.dataset.unavailableText);
      } else {
        self.addMessage({
          kind: 'error',
          heading: self.root.dataset.errorHeading,
          text: self.root.dataset.errorText
        });
        self.announce(self.root.dataset.errorText);
      }
    }).finally(function () {
      self.busy = false;
      self.setThinking(false);
      self.updateComposerState();
      self.input.focus();
    });
  };

  FortuneChatShell.prototype.updateComposerState = function () {
    var length = this.input ? this.input.value.length : 0;
    if (this.characterCount) this.characterCount.textContent = length + ' / ' + this.maxLength;
    if (this.sendButton) this.sendButton.disabled = this.busy || !String(this.input.value || '').trim();
    var conversation = this.currentConversation();
    if (this.exportButton) this.exportButton.disabled = !conversation || !conversation.messages.length;
    this.starters.forEach(function (starter) { starter.disabled = this.busy; }, this);
    if (this.input) this.input.setAttribute('aria-busy', this.busy ? 'true' : 'false');
  };

  FortuneChatShell.prototype.resizeComposer = function () {
    if (!this.input) return;
    this.input.style.height = 'auto';
    this.input.style.height = Math.min(this.input.scrollHeight, 160) + 'px';
  };

  FortuneChatShell.prototype.setThinking = function (active) {
    if (!this.thinking) return;
    this.thinking.hidden = !active;
    if (active && this.log) this.log.scrollTop = this.log.scrollHeight;
  };

  FortuneChatShell.prototype.setActivity = function (label) {
    if (!this.activity) return;
    var visible = Boolean(label);
    if (this.activityText) this.activityText.textContent = visible ? String(label) : '';
    this.activity.hidden = !visible;
  };

  FortuneChatShell.prototype.setProfileAvailable = function (available) {
    if (!this.profileStatus) return;
    var paragraph = this.profileStatus.querySelector('p');
    if (paragraph) paragraph.textContent = available ? this.root.dataset.profileReady : this.root.dataset.profilePending;
    this.profileStatus.classList.toggle('fortune-chat__profile--ready', available);
  };

  FortuneChatShell.prototype.announce = function (message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.setTimeout(function () { this.liveRegion.textContent = String(message || ''); }.bind(this), 20);
  };

  FortuneChatShell.prototype.openSidebar = function () {
    if (!this.mobileQuery.matches || !this.sidebar) return;
    this.drawerOpen = true;
    this.previouslyFocused = document.activeElement;
    this.sidebar.classList.add('fortune-chat-sidebar--open');
    this.sidebar.removeAttribute('inert');
    this.sidebar.setAttribute('aria-hidden', 'false');
    this.sidebar.setAttribute('role', 'dialog');
    this.sidebar.setAttribute('aria-modal', 'true');
    if (this.sidebarOpenButton) this.sidebarOpenButton.setAttribute('aria-expanded', 'true');
    if (this.backdrop) this.backdrop.hidden = false;
    document.documentElement.classList.add('fortune-chat-drawer-open');
    window.requestAnimationFrame(function () { this.sidebarCloseButton && this.sidebarCloseButton.focus(); }.bind(this));
  };

  FortuneChatShell.prototype.closeSidebar = function (restoreFocus) {
    if (!this.sidebar) return;
    this.drawerOpen = false;
    this.sidebar.classList.remove('fortune-chat-sidebar--open');
    if (this.mobileQuery.matches) {
      this.sidebar.setAttribute('aria-hidden', 'true');
      this.sidebar.setAttribute('inert', '');
    }
    this.sidebar.removeAttribute('role');
    this.sidebar.removeAttribute('aria-modal');
    if (this.sidebarOpenButton) this.sidebarOpenButton.setAttribute('aria-expanded', 'false');
    if (this.backdrop) this.backdrop.hidden = true;
    document.documentElement.classList.remove('fortune-chat-drawer-open');
    if (restoreFocus && this.previouslyFocused && this.previouslyFocused.focus) this.previouslyFocused.focus();
  };

  FortuneChatShell.prototype.syncDrawerMode = function () {
    if (!this.sidebar) return;
    if (this.mobileQuery.matches) {
      if (!this.drawerOpen) {
        this.sidebar.setAttribute('aria-hidden', 'true');
        this.sidebar.setAttribute('inert', '');
      }
    } else {
      this.drawerOpen = false;
      this.sidebar.classList.remove('fortune-chat-sidebar--open');
      this.sidebar.removeAttribute('aria-hidden');
      this.sidebar.removeAttribute('inert');
      this.sidebar.removeAttribute('role');
      this.sidebar.removeAttribute('aria-modal');
      if (this.sidebarOpenButton) this.sidebarOpenButton.setAttribute('aria-expanded', 'false');
      if (this.backdrop) this.backdrop.hidden = true;
      document.documentElement.classList.remove('fortune-chat-drawer-open');
    }
  };

  FortuneChatShell.prototype.onDrawerKeydown = function (event) {
    if (!this.drawerOpen || !this.sidebar) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSidebar(true);
      return;
    }
    if (event.key !== 'Tab') return;
    var focusable = Array.from(this.sidebar.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'));
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function init() {
    document.querySelectorAll('[data-fortune-chat]').forEach(function (root) {
      if (root.dataset.fortuneChatReady === 'true') return;
      root.dataset.fortuneChatReady = 'true';
      root.fortuneChatShell = new FortuneChatShell(root);
    });
  }

  window.MoonJadeFortuneUI = window.MoonJadeFortuneUI || {};
  window.MoonJadeFortuneUI.FortuneChatShell = FortuneChatShell;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
